import { chromeFor } from './formats.js'

/**
 * schema.js — the ReportDoc: everything needed to rebuild a case report.
 *
 * This object IS the saved artifact. It goes to Drive as report.json, it is what
 * the builder edits, and its `movements` array is the structured record we want
 * to accumulate across cases to validate the planning engine later. So it is
 * versioned, migrated, and validated rather than being an ad-hoc bag.
 *
 * Two rules shape the design:
 *
 *  1. **Scenes are referenced, not repeated.** A 24-page report draws maybe six
 *     distinct 3D setups. Pages hold a sceneId + a view code; the scene holds the
 *     meshes and colours. Editing "Post Plan Skull" once updates all six pages
 *     that show it — that is what makes a long report fast to build.
 *
 *  2. **Everything positional is normalised (0..1) against the page.** Annotations
 *     and gallery items survive a re-render, a different image resolution, a page
 *     reorder and a template with different margins. Nothing stores millimetres
 *     except geometry.js, which is the page chrome itself.
 */

/**
  * 2 — added by the orbital template: gallery `legend`, grid `cell.crop`, styled
  * info rows, `waiverBlock`, and `page.headerTitle`. Every one is an OPTIONAL
  * field, so a v1 document is still a valid v2 document and migrating is a
  * version stamp. The bump exists so an OLDER build refuses a newer document
  * loudly instead of dropping the legend and printing a volume page with no
  * numbers on it.
  */
export const SCHEMA_VERSION = 3

/** The seven page archetypes. Both shipped reports are built from the first six; `ceph` carries
 * the planning engine's cephalometric analysis VERBATIM — the numbers a surgeon confirmed on
 * screen, never recomputed here. */
export const BLOCKS = ['grid', 'steps', 'measure', 'gallery', 'info', 'text', 'ceph']

/** Standard view codes, matching meshRenderer's VIEW_DEFS plus the isometric. */
export const VIEWS = ['A', 'P', 'LL', 'RL', 'S', 'I', 'ISO', 'ISO2']

export const VIEW_LABELS = {
  A: 'Anterior', P: 'Posterior', LL: 'Left Lateral', RL: 'Right Lateral',
  S: 'Superior', I: 'Inferior', ISO: 'Isometric',
  // A second oblique of the same scene, so one page can show it from two
  // angles. Opt-in at the renderer (`extraViews`) — deliberately NOT in
  // meshRenderer's VIEW_DEFS, because every consumer of renderReport iterates
  // that table and would start emitting an extra view.
  ISO2: 'Isometric (second angle)',
}

/**
 * Which of those views `meshRenderer.renderReport` can actually produce TODAY.
 *
 * Mirrors its VIEW_DEFS, the ISO special case, and the opt-in EXTRA_VIEW_DEFS.
 *
 * `ISO2` joined this set once `meshRenderer` gained its `extraViews` option;
 * before that it was declared and unrenderable, and the orbital recipe dropped
 * it with a note rather than emitting a panel the writer would fill with
 * nothing. That drop path still applies to the next code added ahead of its
 * camera.
 *
 * This set exists so a recipe can ASK for a view it cannot get and be told,
 * rather than emitting a panel the writer then fills with nothing. An
 * unrenderable view used to mean a silent blank with a warning buried in the
 * build log — the "looks finished, isn't" failure this engine exists to remove.
 *
 * When the renderer gains ISO_L, move it in here and every recipe that wanted it
 * starts working with no other change.
 */
export const RENDERABLE_VIEWS = new Set(['A', 'P', 'LL', 'RL', 'S', 'I', 'ISO', 'ISO2'])

/** Is this view drawable today? */
export const isRenderableView = (view) => RENDERABLE_VIEWS.has(String(view ?? '').toUpperCase())

/**
 * Caption slots: a 3×3 ring around the content area, plus a centred sub-caption
 * stack under the top caption.
 *
 * This is not speculative generality — it is the union of what the two shipped
 * reports actually use. Orthognathic pages use tc + bc ("Native Skull" /
 * "Preoperative Images"); fibula measurement pages use tc + sub ("Measurements" /
 * "Proximal" / "Units = mm") + bc ("Distal") + bl (a free note) + br
 * ("Left Fibula"). Nothing in either report needs a slot outside this set.
 */
export const CAPTION_SLOTS = ['tl', 'tc', 'tr', 'bl', 'bc', 'br']

export const emptyCaptions = () => ({
  tl: '', tc: '', tr: '', bl: '', bc: '', br: '', sub: [],
})

/**
 * Deterministic id factory. Ids must be stable across a save/load round trip and
 * reproducible in tests, so they are sequential rather than random.
 */
export function makeIdFactory(prefix = '') {
  const counters = new Map()
  return function nextId(kind) {
    const n = (counters.get(kind) ?? 0) + 1
    counters.set(kind, n)
    return `${prefix}${kind}${String(n).padStart(3, '0')}`
  }
}

// ── constructors ─────────────────────────────────────────────────────────────

export function newReport({ template = 'orthognathic', meta = {} } = {}) {
  // The title lines and the footer's product name are the TEMPLATE's own text,
  // read off its layers and recorded in formats.js with the layer they came
  // from. They used to be written here, per template id, which could state only
  // two of the team's twelve.
  const chrome = chromeFor(template) ?? {
    titleLine1: 'Online Surgical Planning', titleLine2: 'Orthognathic Case Report',
  }
  return {
    schemaVersion: SCHEMA_VERSION,
    template,
    meta: {
      titleLine1: chrome.titleLine1,
      titleLine2: chrome.titleLine2,
      ...(chrome.productName ? { productName: chrome.productName } : {}),
      surgeonName: '', hospitalName: '', patientName: '', caseId: '',
      reportVersion: 'V1.0',
      dateStr: '',
      ...meta,
    },
    library: [],
    scenes: [],
    pages: [],
    movements: [],
  }
}

/**
 * The meta for a sheet whose place in the finished document is unknown — a
 * blank sheet, or one page exported on its own to be merged by hand.
 *
 * Such a sheet carries NO page number, and there is no value a caller could
 * pass that would make numbering it correct: "Page 01 of 1" is a lie on a page
 * whose whole purpose is to be inserted somewhere else. So this forces it off
 * rather than defaulting it, and it is the ONE place that decides it — the
 * blank page and the loose content page both come through here, so the two
 * cannot drift apart. A stitched report is the opposite case: one layout pass
 * knows every page's position, and numbers them.
 */
export const looseSheetMeta = (meta = {}) => ({ ...meta, showPageNumbers: false })


/**
 * A mesh the report can draw.
 * `role` / `stage` / `flags` come from classify.js and drive the default colour;
 * an operator override always wins and is stored on the scene part, not here.
 */
export const newMeshRef = (id, name, origin, classification = {}) => ({
  id, name, origin,
  role: classification.role ?? 'other',
  stage: classification.stage ?? 'native',
  // Which side of the body, or null when the filename does not say — see
  // `classifySide`. `null` is a first-class answer the operator fills in, not a
  // default to be read as "not sided": a fibula report captions five of its
  // pages "Left fibula was used in the reconstruction", so a wrong side is a
  // wrong statement about the patient, and a guess is worse than a blank.
  side: classification.side ?? null,
  flags: classification.flags ?? [],
})

export const newScene = (id, name, parts = [], opts = {}) => ({
  id, name, parts,
  // The meshes' frame, honoured per scene by renderScenes: `flipAP` swaps the A/P camera sides,
  // `lpsFrame` re-sides the lateral cameras and rights S/I (the planning engine's LPS exports).
  // 🔴 STORED ONLY WHEN DECLARED — review root-cause: a stamped default here made "the frame has
  // a boolean" true for every scene ever built, so the render path could not tell a declaration
  // from the default and the operator's global Flip front/back toggle went dead. `null` means
  // "this scene says nothing; the operator's look governs". (The engine's report.json declares
  // its own frames on its raw scenes — see the handoff doc; this constructor never persists a
  // `kind` for the same reason it never invents a frame.)
  frame: opts.frame ?? null,
  style: { mode: 'matcap', brightness: 1.1, ao: true, background: 'white', ...opts.style },
  // Scenes sharing a fitGroup are framed at ONE camera scale, so a Native and a
  // Post-Plan column are directly comparable. Defaults to the scene's own id,
  // i.e. "fit to myself".
  fitGroup: opts.fitGroup ?? id,
})

export const newScenePart = (meshId, color, opts = {}) => ({
  meshId, color,
  group: opts.group ?? null,        // OBJ polygroup, when colouring within one file
  visible: opts.visible ?? true,
  opacity: opts.opacity ?? 1,
})

export const newPage = (id, block, payload = {}) => ({
  id, block,
  frame: null,                      // null → inherit the template's frame style
  captions: emptyCaptions(),
  annotations: [],
  ...payload,
})

export const newGridPage = (id, { rows = 3, cols = 2, badge = 'perCell', cells = [] } = {}) =>
  newPage(id, 'grid', { rows, cols, badge, cells })

/** The six ortho views a full grid page carries, in the team's grid order. Single-sourced here —
 * three copies of this list (draft, pageDraft, renderScenes) were pinned to nothing and could
 * reorder independently, changing half the reports. (SPE's own copy is parity-pinned to
 * shared/report-look.json and is deliberately separate.) */
export const GRID_VIEWS = ['A', 'P', 'LL', 'RL', 'S', 'I']

/**
 * The four a long bone is shown in, in the TEMPLATE'S OWN ORDER — the laterals sit
 * between the two frontal views, not after them.
 *
 * Measured from both supplied references, badge x in mm across a frame that spans
 * 3.7 → 206.2 (quarters at 54.4, 105.0, 155.6):
 *   planning images,  `Native Leg`      A 15.1 · LL 63.1 · P 105.0 · RL 157.4
 *   fibula report,    `Page 4 Native Legs`  A 15.2 · LL 63.2 · P 105.2 · RL 157.5
 * One badge per quarter. A, P, LL, RL would print P where every delivered report
 * has LL.
 */
export const FOUR_VIEWS = ['A', 'LL', 'P', 'RL']

/** The 3×2 six-view page shape — ONE assembly for every draft path (recipe, SPE-scene,
 * page-organised). Captions and recipe/source identity are the caller's. */
export const sixViewGridPage = (id, sceneId, views = GRID_VIEWS) => ({
  ...newGridPage(id, {
    rows: 3, cols: 2, badge: 'perCell',
    cells: views.map((v) => newGridCell(sceneId, v)),
  }),
  tightGroup: true,   // see SHARED_CROP below
})


/**
 * One scene, a row of chosen views, one frame.
 *
 * The fibula template's leg pages: native legs 4 views, leg-and-fibula 3,
 * leg-with-guide 3, counted from the reference dump — see the measured list in
 * template.js, including the trap that the badge LAYERS are not the views. The two
 * fibula guide pages, 4, at the team's request of 19 Oct 2026.
 * `formats.js` states each page's own views; four is the default.
 */
export const viewRowPage = (id, sceneId, views) => ({
  ...newGridPage(id, {
    rows: 1, cols: views.length, badge: 'perCell',
    cells: views.map((v) => newGridCell(sceneId, v)),
  }),
  tightGroup: true,
  dividers: false,
})

/**
 * SHARED_CROP — why the three page shapes below all carry `tightGroup: true`.
 *
 * Every cell of these pages shows ONE scene at ONE camera scale, so each render is
 * framed to whichever view needs the most room and the rest sit small in their
 * cells. The writer crops them all to a single rectangle, the union of their ink,
 * so they grow together and the shared scale survives. Measured 1 Oct 2026 on the
 * team's fibula report, the model's box over its cell: before 61.3 % (55-67 %),
 * after 81.2 % (71-91 %).
 *
 * NOT the per-image `tight`, which crops each view to its own box: six views at six
 * different scales can no longer be read against one another.
 *
 * The team asked for it twice, 28 Sep on the comparison pages and 1 Oct on the
 * mandible pages. `layout.js` also infers it from a page's shape, so a stored
 * document drafted before the field existed fills its cells like a fresh one.
 */

/**
 * The quality waiver as the source-data page prints it.
 *
 * ONE builder, because three places need it — the generic CT page, the orbital
 * recipe and the orthognathic source-data page — and a second copy is how the
 * orthognathic page came to show the Builder's waiver panel while printing
 * nothing. Returns null when no waiver was opened at all, so the slot stays empty.
 *
 * The value is the statement, not the notes: an unticked waiver prints "No" and
 * drops any notes left behind by ticking and unticking.
 */
export const WAIVER_RED = [192, 57, 43]

export const waiverBlockFor = (waiver) => (waiver
  ? {
    label: 'Quality waiver',
    value: waiver.waived ? 'Yes' : 'No',
    valueStyle: waiver.waived ? { color: WAIVER_RED, bold: true } : undefined,
    notes: waiver.waived ? (waiver.notes ?? '') : '',
  }
  : null)

/** The 3×2 left/right comparison shape: each of three view codes shown for BOTH scenes,
 * left column then right. */
export const comparisonGridPage = (id, left, right, views) => ({
  ...newGridPage(id, {
    rows: 3, cols: 2, badge: 'perCell',
    cells: views.flatMap((v) => [v, v]).map((v, i) => newGridCell(i % 2 === 0 ? left : right, v)),
  }),
  tightGroup: true,   // see SHARED_CROP above
})

export const newGridCell = (sceneId = null, view = 'A') => ({
  sceneId, view, override: null, annotations: [],
})

export const newTextPage = (id, body = []) => newPage(id, 'text', { body })

/**
 * `info` — Source data information (reference p2). `groups` are titled
 * label/value blocks; `images` are up to two renders of what that data produced.
 */
export const newInfoPage = (id, { groups = [], images = [], summary = '' } = {}) =>
  newPage(id, 'info', { groups, images, summary })

/** `steps` — the planning narrative, one panel per step (reference pp. 5-6). */
export const newStepsPage = (id, { cols = 2, steps = [] } = {}) =>
  newPage(id, 'steps', { cols, steps })

export const newStep = (label, text, { sceneId = null, view = 'A', annotations = [] } = {}) =>
  ({ label, text, sceneId, view, annotations })

/**
 * `ceph` — the cephalometric analysis handed over by the planning engine
 * (`report.json` → `meta.cephalometry`). The payload travels UNTOUCHED: the classic trio with its
 * reading sentences, the Burstone (COGS) table with each value's published band and verdict, the
 * chosen norm set WITH its citation, and the frontal comparative rows when they exist. This block
 * renders what the operator saw; it must never edit, recompute or re-round a number — a report is
 * read later, elsewhere, by someone who was not in the room.
 */
export const newCephPage = (id, { ceph = null } = {}) => newPage(id, 'ceph', { ceph })

/**
 * Does this cephalometry payload carry an ANALYSIS worth a page? One predicate, used by the
 * Builder, the draft recipe and the validator — review finding: the `ceph.angles` sentinel was
 * re-implemented at all three layers, and it was WRONG at the contract level. The producer's own
 * design makes angles-absent-but-measured a legitimate state: the classic trio can be refused or
 * locked while 25 Burstone (COGS) measurements or the frontal rows are surgeon-confirmed. Those
 * must never vanish because the trio did.
 */
export const hasCephAnalysis = (ceph) => !!(ceph && (
  ceph.angles
  || (ceph.cogs?.measured?.length)
  || (ceph.frontal?.measured?.length)
))

/** `measure` — stacked measurement panels (reference pp. 7-8, 13). */
export const newMeasurePage = (id, { rows = [], notes = '' } = {}) => newPage(id, 'measure', { rows, notes })

/** `gallery` — print models with leader callouts (reference pp. 20-21). */
export const newGalleryPage = (id, { items = [], cols = 1 } = {}) => newPage(id, 'gallery', { items, cols })

/**
 * One planned movement. This is the record that drives the step pages' arrows,
 * arcs and captions — and the reason the whole document is worth storing as
 * data: across many cases it becomes a validation corpus for the planner.
 */
export const newMovement = (id, n, fields = {}) => ({
  id, n,
  procedure: 'custom',   // lefort1 | bsso | genioplasty | fibulaSegment | custom
  segment: '',
  kind: 'advance',       // advance | setback | impact | rotate | cut | osteotomy
  value: 0,
  unit: 'mm',            // mm | deg
  axis: '',
  direction: '',
  note: '',
  ...fields,
})

/** An overlay drawn on top of a page or a cell. Coordinates are 0..1. */
export const newAnnotation = (id, kind, fields = {}) => ({
  id, kind,             // text | arrow | angleArc | leader | circle | dimension | inset
  ...fields,
})

// ── validation ───────────────────────────────────────────────────────────────

/**
 * Structural problems in a report, as a flat list the UI can show inline.
 *
 * Deliberately reports rather than throws, and deliberately separates `errors`
 * (the document is broken — a page points at a scene that does not exist) from
 * `warnings` (the document is valid but incomplete — an empty cell). A half-built
 * report is the normal state in a builder; only errors block an export.
 */
export function validateReport(doc) {
  const errors = []
  const warnings = []
  const at = (where, message, severity = 'error') =>
    (severity === 'error' ? errors : warnings).push({ where, message })

  if (!doc || typeof doc !== 'object') return { errors: [{ where: 'doc', message: 'not an object' }], warnings }
  if (doc.schemaVersion !== SCHEMA_VERSION) {
    at('schemaVersion', `expected ${SCHEMA_VERSION}, got ${doc.schemaVersion} — run migrateReport() first`)
  }

  const meshIds = new Set((doc.library ?? []).map((m) => m.id))
  const sceneIds = new Set((doc.scenes ?? []).map((s) => s.id))
  const movementIds = new Set((doc.movements ?? []).map((m) => m.id))

  const dupes = (list, label) => {
    const seen = new Set()
    for (const item of list ?? []) {
      if (seen.has(item.id)) at(label, `duplicate id "${item.id}"`)
      seen.add(item.id)
    }
  }
  dupes(doc.library, 'library')
  dupes(doc.scenes, 'scenes')
  dupes(doc.pages, 'pages')
  dupes(doc.movements, 'movements')

  for (const scene of doc.scenes ?? []) {
    if (!scene.parts?.length) at(`scene ${scene.id}`, 'has no parts', 'warning')
    for (const part of scene.parts ?? []) {
      if (!meshIds.has(part.meshId)) {
        at(`scene ${scene.id}`, `part references unknown mesh "${part.meshId}"`)
      }
    }
  }

  for (const [i, page] of (doc.pages ?? []).entries()) {
    const where = `page ${i + 1} (${page.id})`
    if (!BLOCKS.includes(page.block)) at(where, `unknown block "${page.block}"`)

    if (page.block === 'ceph') {
      if (!page.ceph || typeof page.ceph !== 'object' || !hasCephAnalysis(page.ceph)) {
        at(where, 'ceph page carries no analysis (no angles, COGS or frontal measurements)')
      }
    }

    if (page.block === 'grid') {
      const capacity = page.rows * page.cols
      if (!(page.rows > 0 && page.cols > 0)) at(where, `bad grid ${page.rows}×${page.cols}`)
      if ((page.cells?.length ?? 0) > capacity) {
        at(where, `${page.cells.length} cells in a ${page.rows}×${page.cols} grid`)
      }
      for (const [ci, cell] of (page.cells ?? []).entries()) {
        if (cell.sceneId && !sceneIds.has(cell.sceneId)) {
          at(where, `cell ${ci + 1} references unknown scene "${cell.sceneId}"`)
        }
        if (cell.view && !VIEWS.includes(cell.view)) {
          at(where, `cell ${ci + 1} has unknown view "${cell.view}"`)
        }
        if (!cell.sceneId && !cell.override) {
          at(where, `cell ${ci + 1} is empty`, 'warning')
        }
      }
    }

    if (page.block === 'steps') {
      for (const [si, step] of (page.steps ?? []).entries()) {
        if (step.movementId && !movementIds.has(step.movementId)) {
          at(where, `step ${si + 1} references unknown movement "${step.movementId}"`)
        }
      }
    }
  }

  return { errors, warnings }
}

/**
 * Bring an older saved document up to SCHEMA_VERSION.
 *
 * Every migration appends a step here; none of them may mutate the input.
 * A saved report on Drive must never be orphaned by a schema change.
 */
export function migrateReport(doc) {
  if (!doc || typeof doc !== 'object') throw new TypeError('migrateReport: not a document')
  if (doc.schemaVersion === SCHEMA_VERSION) return doc
  if (doc.schemaVersion > SCHEMA_VERSION) {
    throw new Error(
      `report.json is schemaVersion ${doc.schemaVersion} but this build only understands ` +
      `${SCHEMA_VERSION} — it was saved by a newer version of the tool. Update, don't downgrade: ` +
      'opening it here would silently drop whatever the newer schema added.',
    )
  }
  // 1 → 2 → 3: every addition at both steps is an OPTIONAL field, so an older
  // document is already structurally valid. Re-stamp it rather than rewriting
  // anything — the planning engine's report.json is v1 and must keep loading
  // untouched. v3 added `page.dividers`; a document that predates it simply has
  // none, and a grid page without it rules between its cells as it always did.
  if (doc.schemaVersion === 1 || doc.schemaVersion === 2) {
    return { ...doc, schemaVersion: SCHEMA_VERSION }
  }
  throw new Error(`migrateReport: no migration path from schemaVersion ${doc.schemaVersion}`)
}
